import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useDeleteAsset, useDomains, useZones } from '@/lib/queries';
import { assetDownloadUrl } from '@/lib/api';
import type { AssetWithTags } from '@/lib/types';
import { Download, Trash2 } from 'lucide-react';

/**
 * Bottom-sheet lightbox. Full-bleed image at the top, metadata
 * + controls beneath. Sized to ~90vh so the image gets the
 * dominant share of the screen on a phone.
 */
export default function PhotoLightbox({
  asset,
  onClose,
}: {
  asset: AssetWithTags | null;
  onClose: () => void;
}) {
  const del = useDeleteAsset();
  const domains = useDomains();
  const zones = useZones();
  if (!asset) return null;

  const domain = (domains.data ?? []).find((d) => d.id === asset.domainId);
  const zone = (zones.data ?? []).find((z) => z.id === asset.zoneId);

  async function onDelete() {
    if (!asset) return;
    if (!confirm('Delete this photo? This cannot be undone.')) return;
    await del.mutateAsync(asset.id);
    onClose();
  }

  return (
    <Sheet open={!!asset} onOpenChange={(o) => (o ? null : onClose())}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto px-0 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
        <SheetHeader className="px-5">
          <SheetTitle>{asset.caption || asset.originalFilename || 'Photo'}</SheetTitle>
          <SheetDescription className="font-mono text-[11px] uppercase tracking-wider">
            {new Date(asset.uploadedAt).toLocaleString()}
          </SheetDescription>
        </SheetHeader>

        {asset.mimeType?.startsWith('image/') ? (
          <div className="my-4 bg-foreground/5">
            <img
              src={assetDownloadUrl(asset.id)}
              alt={asset.caption ?? asset.originalFilename ?? 'Photo'}
              className="block w-full max-h-[60vh] object-contain"
            />
          </div>
        ) : (
          <div className="my-4 bg-muted px-5 py-10 text-center text-muted-foreground">
            <p className="font-mono text-xs">{asset.mimeType ?? 'file'}</p>
            <p className="text-sm pt-2">{asset.originalFilename ?? 'attachment'}</p>
          </div>
        )}

        <div className="px-5 space-y-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {domain ? (
              <span className="font-mono uppercase tracking-wider">{domain.name}</span>
            ) : null}
            {zone ? (
              <span className="font-mono uppercase tracking-wider">{zone.name}</span>
            ) : null}
            {asset.sizeBytes != null ? (
              <span className="font-mono tabular-nums">
                {(asset.sizeBytes / 1024).toFixed(0)} KB
              </span>
            ) : null}
          </div>

          {asset.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {asset.tags.map((t) => (
                <Badge key={t} variant="secondary" className="font-mono text-[10px] tracking-wider">
                  {t}
                </Badge>
              ))}
            </div>
          ) : null}

          <div className="flex items-center gap-2 pt-2">
            <Button asChild variant="secondary">
              <a href={assetDownloadUrl(asset.id)} download={asset.originalFilename ?? undefined}>
                <Download className="h-4 w-4 mr-2" aria-hidden /> Download
              </a>
            </Button>
            <Button
              onClick={onDelete}
              variant="ghost"
              className="text-destructive hover:text-destructive"
              data-testid="photo-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
